import {
  QueryKey,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback } from 'react';

type T<
  Model,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
> = UseQueryOptions<Model, TError, Model, TQueryKey>;

const useManualSingleQuery = <
  Model extends { id: number },
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: T<Model, TError, TQueryKey>,
) => {
  const queryClient = useQueryClient();

  const queryKey = options.queryKey as QueryKey;

  const get = useCallback(() => {
    const data = queryClient.getQueryData<Model>(queryKey);
    return data;
  }, [queryClient, queryKey]);

  const set = useCallback(
    (data: Model) => {
      queryClient.setQueryData<Model>(queryKey, data);
    },
    [queryClient, queryKey],
  );

  const update = useCallback(
    (data: Partial<Model>) => {
      const currentData = queryClient.getQueryData<Model>(queryKey);

      if (!currentData) return;

      queryClient.setQueryData<Model>(queryKey, {
        ...currentData,
        ...data,
      });
    },
    [queryClient, queryKey],
  );

  const remove = useCallback(() => {
    queryClient.setQueryData<Model>(queryKey, undefined);
  }, [queryClient, queryKey]);

  return { get, set, update, remove };
};

export default useManualSingleQuery;
